import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useHistory, useParams } from "react-router";
import Page from "components/page/Page";
import { StatusCode } from "api/protocols";
import {
  GetEmailCampaigns,
  IEmailCampaign,
  PutPublishEmailCampaign,
  PutScheduleEmailCampaign,
} from "api/rpc/2024-04/facilityAdmin/marketing/emailCampaigns";
import { showError, showSuccess } from "redux/actions/ui";
import { resolveEmailCampaignFinalPublishDecision } from "./emailCampaignDesignStatus";
import EmailCampaignSchedulePanel from "./EmailCampaignSchedulePanel";
import {
  buildEmailCampaignSchedulePayloadFromCampaign,
  isEmailCampaignScheduleCompleteFromCampaign,
  TEmailCampaignScheduleFields,
} from "./emailCampaignScheduleState";

export default function ScheduleEmailCampaign() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const { id: campaignIdParam } = useParams<{ id: string }>();
  const campaignId = Number(campaignIdParam);

  const [campaign, setCampaign] = useState<IEmailCampaign | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);

  const canPublish = campaign != null
    && isEmailCampaignScheduleCompleteFromCampaign(campaign)
    && !publishing
    && !saving;

  useEffect(() => {
    void loadCampaign();
  }, [campaignId]);

  async function loadCampaign() {
    if (!Number.isFinite(campaignId) || campaignId < 1) {
      dispatch(showError(t("Could not find email campaign")));
      history.push("/admin/email-marketing");
      return;
    }

    setLoading(true);
    const resp = await GetEmailCampaigns({ id: campaignId }, true);

    if (resp.status !== StatusCode.OK || resp.data.length === 0) {
      dispatch(showError(t("Could not find email campaign")));
      history.push("/admin/email-marketing");
      return;
    }

    const loaded = resp.data[0];
    if (loaded == null) {
      dispatch(showError(t("Could not find email campaign")));
      history.push("/admin/email-marketing");
      return;
    }

    if (loaded.status !== "draft") {
      dispatch(showError(t("Campaign is not editable")));
      history.push(`/admin/email-marketing/campaigns/${loaded.id}`);
      return;
    }

    setCampaign(loaded);
    setLoading(false);
  }

  function updateCampaignSchedule(fields: TEmailCampaignScheduleFields) {
    setCampaign(prev => (prev == null ? prev : { ...prev, ...fields }));
  }

  async function saveSchedule(): Promise<boolean> {
    if (campaign == null) {
      return false;
    }

    const payload = buildEmailCampaignSchedulePayloadFromCampaign(campaign.id, campaign);
    if (payload == null) {
      dispatch(showError(t("Choose when to send this campaign.")));
      return false;
    }

    setSaving(true);
    try {
      const resp = await PutScheduleEmailCampaign(payload, true);
      if (resp.status !== StatusCode.OK) {
        dispatch(showError(resp.message || t("Could not save schedule settings.")));
        return false;
      }

      if (resp.data != null && typeof resp.data === "object" && "id" in resp.data) {
        setCampaign(prev => (prev == null ? prev : { ...prev, ...resp.data }));
      }
      return true;
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveAndExit() {
    if (campaign?.scheduled_type == null) {
      history.push("/admin/email-marketing");
      return;
    }

    if (!(await saveSchedule())) {
      return;
    }

    dispatch(showSuccess(t("Schedule settings saved")));
    history.push("/admin/email-marketing");
  }

  async function handlePublishCampaign() {
    if (campaign == null || !canPublish) {
      return;
    }

    const publishDecision = resolveEmailCampaignFinalPublishDecision({
      design: campaign.design,
    });

    if (publishDecision !== "allowed") {
      dispatch(
        showError(
          publishDecision === "design_required"
            ? t("Design the email in the email builder before publishing this campaign.")
            : t("Finalize your email design in the builder before publishing this campaign."),
        ),
      );
      return;
    }

    if (!(await saveSchedule())) {
      return;
    }

    setPublishing(true);
    try {
      const resp = await PutPublishEmailCampaign({ id: campaign.id }, true);
      if (resp.status !== StatusCode.OK) {
        dispatch(showError(resp.message));
        return;
      }

      dispatch(showSuccess(resp.message || t("Campaign published")));
      history.push("/admin/email-marketing");
    } finally {
      setPublishing(false);
    }
  }

  if (loading || campaign == null) {
    return (
      <Page
        title={t("Schedule email")}
        subtitle={t("Choose when your campaign will be delivered to your audience.")}
        breadcrumbs={[
          {
            prefix: true,
            label: t("Back"),
            url: `/admin/email-marketing/campaigns/${campaignId}`,
          },
        ]}
      />
    );
  }

  return (
    <Page
      className="[&_.ui-page-actions>.rc-button+.rc-button]:ml-0"
      title={t("Schedule email")}
      subtitle={t("Choose when your campaign will be delivered to your audience.")}
      primaryAction={{
        content: publishing ? t("Scheduling…") : t("Schedule campaign"),
        action: () => {
          void handlePublishCampaign();
        },
        disabled: !canPublish,
      }}
      secondaryActions={[
        {
          content: saving ? t("Saving…") : t("Save & exit"),
          action: () => {
            void handleSaveAndExit();
          },
          disabled: saving || publishing,
        },
      ]}
      breadcrumbs={[
        {
          prefix: true,
          label: t("Back"),
          url: `/admin/email-marketing/campaigns/${campaign.id}`,
        },
      ]}
    >
      <EmailCampaignSchedulePanel
        value={{
          scheduled_type: campaign.scheduled_type,
          scheduled_at: campaign.scheduled_at,
          scheduled_recurring_type: campaign.scheduled_recurring_type,
        }}
        onChange={updateCampaignSchedule}
      />
    </Page>
  );
}
